import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MapPin, Loader2, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLS = [
  { key: 'site',              label: 'Site',       align: 'left',  type: 'string' },
  { key: 'calls',             label: 'Calls',      align: 'right', type: 'number' },
  { key: 'avg_score',         label: 'Avg Score',  align: 'right', type: 'number' },
  { key: 'closed',            label: 'Closed',     align: 'right', type: 'number' },
  { key: 'revivable',         label: 'Revivable',  align: 'right', type: 'number' },
  { key: 'lost',              label: 'Lost',       align: 'right', type: 'number' },
  { key: 'missed',            label: 'Missed',     align: 'right', type: 'number' },
  { key: 'talk_time_seconds', label: 'Talk Time',  align: 'right', type: 'number' },
  { key: 'coaching_needed',   label: 'Coaching',   align: 'right', type: 'number' },
];

const _fmtTalk = (s) => {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}m ${sec.toString().padStart(2, '0')}s`;
};

const _scoreColor = (s) =>
  s == null ? 'text-slate-400'
  : s >= 70 ? 'text-emerald-600'
  : s >= 55 ? 'text-amber-600'
  : 'text-red-600';

/**
 * SiteScoreboardCard — one row per site with the exec-level scoreboard:
 * Calls / Avg Score / Closed / Revivable / Lost / Missed / Talk Time /
 * Coaching-Needed. Clickable column headers toggle sort direction.
 * Site A→Z is the default so leaders can scan by branch first.
 */
const SiteScoreboardCard = ({ domainId = null, days = 30 }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('site');
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ days: String(days) });
        if (domainId) params.set('domain_id', domainId);
        const r = await axios.get(`${API}/analytics/site-scoreboard?${params}`);
        if (!cancelled) setRows(r.data?.sites || []);
      } catch {
        if (!cancelled) setRows([]);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [domainId, days]);

  const sorted = useMemo(() => {
    const col = COLS.find((c) => c.key === sortKey) || COLS[0];
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      // Always sink "Unassigned" to bottom regardless of direction so the
      // primary tenant data stays on top.
      if (a.site === 'Unassigned' && b.site !== 'Unassigned') return 1;
      if (b.site === 'Unassigned' && a.site !== 'Unassigned') return -1;
      if (col.type === 'string') {
        const s = String(av || '').localeCompare(String(bv || ''));
        return sortDir === 'asc' ? s : -s;
      }
      const an = av == null ? -Infinity : Number(av);
      const bn = bv == null ? -Infinity : Number(bv);
      return sortDir === 'asc' ? an - bn : bn - an;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Sensible default: string ascending, numeric descending.
      const col = COLS.find((c) => c.key === key);
      setSortDir(col?.type === 'string' ? 'asc' : 'desc');
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="site-scoreboard-card">
      <CardHeader className="flex flex-row items-center justify-between pb-3 gap-3 flex-wrap">
        <CardTitle className="text-base flex items-center gap-2">
          <MapPin className="w-4 h-4 text-teal-600" />
          Performance by Site
          <span className="text-xs font-normal text-slate-400">last {days} days</span>
        </CardTitle>
        <div className="flex items-center gap-3">
          <label
            className="inline-flex items-center gap-1.5 cursor-pointer"
            data-testid="site-scoreboard-toggle-site-sort"
          >
            <input
              type="checkbox"
              checked={sortKey === 'site'}
              onChange={(e) => {
                // When ticked, snap back to Site A→Z. When unticked, fall
                // back to the natural "biggest team first" view (Calls desc).
                if (e.target.checked) {
                  setSortKey('site');
                  setSortDir('asc');
                } else {
                  setSortKey('calls');
                  setSortDir('desc');
                }
              }}
              className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-400"
            />
            <span className="text-xs font-semibold text-slate-700">
              Sort by site (A → Z)
            </span>
          </label>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-300" />}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading && rows.length === 0 ? (
          <div className="text-center text-slate-400 text-xs py-8">Loading site scoreboard…</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-slate-400 text-xs py-8">
            No site data yet — map extensions to sites under the Domain → Overview tab.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="site-scoreboard-table">
              <thead>
                <tr className="text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-200">
                  {COLS.map((c) => {
                    const active = sortKey === c.key;
                    const Arrow = sortDir === 'asc' ? ArrowUp : ArrowDown;
                    return (
                      <th
                        key={c.key}
                        onClick={() => toggleSort(c.key)}
                        className={`py-2 px-2 cursor-pointer select-none hover:text-slate-800 ${
                          c.align === 'right' ? 'text-right' : 'text-left'
                        } ${active ? 'text-teal-700' : ''}`}
                        data-testid={`sort-${c.key}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {c.label}
                          {active && <Arrow className="w-3 h-3" />}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((s) => {
                  // Deep-link URLs preserve the current pathname (works for
                  // both /dashboard and /admin/domains/:id) and add
                  // ?tab=revivable&site=<name> so the target tab picks up
                  // the site chip from the deep-link effect.
                  const _base = window.location.pathname;
                  const _q = (tab, site) => `${_base}?tab=${tab}&site=${encodeURIComponent(site)}`;
                  return (
                  <tr key={s.site} data-testid={`site-row-${s.site}`}>
                    <td className="py-2 px-2 font-semibold text-slate-800">
                      {s.site}
                      {s.extensions?.length > 0 && (
                        <span className="ml-1 text-[10px] font-normal text-slate-400">
                          · {s.extensions.length} ext
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-700">{s.calls.toLocaleString()}</td>
                    <td className={`py-2 px-2 text-right tabular-nums font-semibold ${_scoreColor(s.avg_score)}`}>
                      {s.avg_score == null ? '—' : Math.round(s.avg_score)}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums text-emerald-700 font-semibold">{s.closed}</td>
                    <td className="py-2 px-2 text-right tabular-nums">
                      {s.revivable > 0 ? (
                        <button
                          onClick={() => navigate(_q('revivable', s.site))}
                          className="text-amber-700 font-semibold hover:underline hover:text-amber-800"
                          title={`Open ${s.revivable} revivable deal${s.revivable === 1 ? '' : 's'} for ${s.site}`}
                          data-testid={`site-cell-revivable-${s.site}`}
                        >{s.revivable}</button>
                      ) : (
                        <span className="text-slate-300">0</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">
                      {s.lost > 0 ? (
                        <button
                          onClick={() => navigate(_q('revivable', s.site))}
                          className="text-red-700 font-semibold hover:underline hover:text-red-800"
                          title={`Open revivable queue filtered to ${s.site} (lost deals surface via 'Include contacted')`}
                          data-testid={`site-cell-lost-${s.site}`}
                        >{s.lost}</button>
                      ) : (
                        <span className="text-slate-300">0</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-600">{s.missed}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-600">{_fmtTalk(s.talk_time_seconds)}</td>
                    <td className="py-2 px-2 text-right">
                      {s.coaching_needed > 0 ? (
                        <button
                          onClick={() => navigate(_q('coaching', s.site))}
                          className="inline-block bg-red-100 hover:bg-red-200 text-red-700 font-bold px-2 py-0.5 rounded-full text-[11px] transition"
                          title={`Open coaching queue for ${s.site}`}
                          data-testid={`site-cell-coaching-${s.site}`}
                        >
                          {s.coaching_needed} rep{s.coaching_needed === 1 ? '' : 's'}
                        </button>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SiteScoreboardCard;
